import convert from "convert-units";
import _ from "lodash";
import React from "react";
import { withTranslation } from "react-i18next";
import submit from "submit-form";
import { PARAMETER_OPTIONS } from "../../../../constants";
import { mapParameter } from "../../../../parts/other-exercise/CustomParametersSets";
import { RillContext } from "../../../../rill/RillReact/share/base";
import { anySetCompleted } from "../../../../utils/actuals";
import { isNotMac } from "../../../../utils/browser";
import fromIntensity from "../../../../utils/get-zone";
import {
  buildSupersets,
  calculatePrefixForExercise,
  reorderBreaksSuperset,
} from "../../../../utils/prefix-calculator";
import WorkoutParamsRow from "../../../athlete-portal/calendar/components/EditMode/ExerciseEditor/WorkoutParamsRow";
import ExerciseMetrics from "../../calendar/components/ExerciseMetrics";
import ExerciseLineItem from "./ExerciseLineItem";
import NotesWrapper from "./NotesWrapper";
import i18n from "../../../../i18n";
import { splitRpeText } from "../../../../utils/rpe-range";

const { LOAD } = PARAMETER_OPTIONS;

class EditorSessionTableRow extends React.Component {
  constructor(props, ctx) {
    super(props, ctx);
    this.state = {
      currentRow: 0,
      isAddingSet: false,
      newMovement: "",
      sets: 0,
      reps: 0,
      rpe: "",
      load: 0,
      percentRepMax: 0,
      expanded: true,
    };

    this.localLoadInputs = [];
    this.localIntensityInputs = [];

    this.handleReorder = this.handleReorder.bind(this);
    this._handleOpenAddSet = this._handleOpenAddSet.bind(this);
    this._handleCloseAddSet = this._handleCloseAddSet.bind(this);
    this._handleNewSetChange = this._handleNewSetChange.bind(this);
    this._handleSaveNewSet = this._handleSaveNewSet.bind(this);
    this._handleNewMovementChange = this._handleNewMovementChange.bind(this);
    this._handleSaveNewSetForOther = this._handleSaveNewSetForOther.bind(this);
    this._handleCreateSuperset = this._handleCreateSuperset.bind(this);
    this._handleRemoveSuperset = this._handleRemoveSuperset.bind(this);
    this.toggleExpanded = this.toggleExpanded.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.exercise !== this.props.exercise) {
      this.setState({ newMovement: "" });
    }
  }

  hasNumber(string) {
    return /\d/.test(string);
  }

  handleReorder({ exercise, _exercises, to, isProgramEditor, athlete, e }) {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    // handle optimistic updates
    const exerciseChangedIndex = this.props.exercises.findIndex(
      (ex) => ex._id === exercise
    );
    // Build the prospective order after this single-step move and block it only
    // when it would split a genuine superset/complex group. Stale (ghost)
    // nextSuperset pointers form singletons in buildSupersets, so they no longer
    // block reordering. See reorderBreaksSuperset in prefix-calculator.
    const newOrder = this.props.exercises.map((ex) => ex._id);
    const targetIndex = exerciseChangedIndex + to;
    if (targetIndex >= 0 && targetIndex < newOrder.length) {
      const [movedId] = newOrder.splice(exerciseChangedIndex, 1);
      newOrder.splice(targetIndex, 0, movedId);
    }

    if (reorderBreaksSuperset(this.props.exercises, newOrder)) {
      alert(this.props.t("editorSessionTableRow.cannotReorderWithinSuperset"));
      return;
    }

    if (isProgramEditor) {
      submit(`/actions/exercise/order?proxyAthlete=${athlete}`, {
        method: "POST",
        body: {
          cycle: this.props.cycle._id,
          program: this.props.programOrder,
          session: this.props.session.order,
          week: this.props.session.week,
          exercises: this.props.exercises,
          exercise,
          to,
        },
      });
    } else {
      submit(`/actions/exercise/single-workout-order?proxyAthlete=${athlete}`, {
        method: "POST",
        body: {
          exercises: this.props.exercises,
          exercise,
          to,
        },
      });
    }
  }

  // Add Set functions
  // handles the opening of the Add set line
  _handleOpenAddSet(e, setIndex) {
    e.preventDefault();
    this.setState({ isAddingSet: true, currentRow: setIndex });
  }
  // handles the closing of the Add set line
  // if the add set line is closed, we should reset all the states
  _handleCloseAddSet(e) {
    e.preventDefault();
    this.setState({
      isAddingSet: false,
      currentRow: 0,
      sets: 0,
      reps: 0,
      rpe: "",
      load: 0,
      percentRepMax: 0,
    });
  }
  // handles any changes for the fields in the add set line
  _handleNewSetChange({ target: { name, value } }, defaultRepMax) {
    const activeExercise = _.find(this.props.exercises, {
      _id: this.props.exercise,
    });
    const isAssistant = activeExercise.type === "assistant";

    // If the field is load or pRM, calculate and set states.
    if (name === "load" || name === "percentRepMax") {
      // if name is load
      if (name === "load") {
        this.setState({
          percentRepMax: isAssistant
            ? 0
            : _.round((value / defaultRepMax) * 100, 1),
          load: value,
        });
      }
      // else if name is percentRepMax
      else if (name === "percentRepMax" && !isAssistant) {
        this.setState({
          load: _.round((defaultRepMax * value) / 100, 2),
          percentRepMax: value,
        });
      }
      if (!isAssistant) {
        // recalculating the fields to be shown.
        const localLoadInput = this.localLoadInputs.new;
        if (localLoadInput) {
          if (name === "percentRepMax") {
            localLoadInput.value = _.round((defaultRepMax * value) / 100, 2);
          } else {
            this.localIntensityInputs.new.value = _.round(
              (value / defaultRepMax) * 100,
              1
            );
          }
        }
      }
    }
    // else set state dynamically
    else {
      this.setState({ [name]: value });
    }
  }
  validateSaveNewSet(newSet, isAssistant, defaultRepMax) {
    if (
      newSet.sets <= 0 ||
      newSet.sets > 500 ||
      (!isAssistant && defaultRepMax <= 0) ||
      (newSet.rpe !== "AMRAP" && newSet.reps <= 0)
    )
      return false;

    return true;
  }

  // adds the newly entered set into the exercise's quick-edit local state
  _handleSaveNewSet(e, isEquipped, defaultRepMax, _activeUser, exId, setIndex) {
    e.preventDefault();
    const { sets, reps, rpe, load, percentRepMax, repsTwo, repsThree } =
      this.state;
    const { units: coachUnits } = this.context.locals.activeUser;
    const isRepRange = String(reps).includes("-");
    const repsRange = isRepRange ? reps.split("-") : reps;

    const exercise = this.props.exercises.find(
      (ex) => ex._id === this.props.exercise
    );
    const isAssistant = exercise.type === "assistant";
    let complexExercises = [];
    if (exercise.complex && exercise.complexInstance) {
      complexExercises = (
        this.props.ungroupedExercises || this.props.exercises
      ).filter(
        (ex) =>
          ex.complexInstance === exercise.complexInstance &&
          ex.complex === exercise.complex &&
          ex._id !== exId
      );
    }

    const newSet = {
      sets,
      reps: isRepRange ? repsRange[1] || repsRange[0] : reps,
      percentRepMax,
      load:
        coachUnits === "imperial"
          ? _.round(convert(load).from("lb").to("kg"), 2)
          : load,
      equipped: isEquipped,
      zone: fromIntensity(percentRepMax, reps),
      rpe,
      repsLower: isRepRange && repsRange[1] ? repsRange[0] : null,
    };

    const rpeError = splitRpeText(newSet);
    if (rpeError) {
      this.context.locals.flash("error", i18n.t(`common:${rpeError}`));
      return;
    }

    if (this.validateSaveNewSet(newSet, isAssistant, defaultRepMax)) {
      this.props.handleAddSetLocally(
        exId,
        setIndex,
        newSet,
        complexExercises.map((ex) => ex._id),
        repsTwo,
        repsThree
      );
    }

    this._handleCloseAddSet(e);
  }

  // adds the newly entered custom-parameter set into the exercise's
  // quick-edit local state
  _handleSaveNewSetForOther(e, exId, setIndex, _activeUser, activeExercise) {
    e.preventDefault();

    const newSet = {};
    const { units: coachUnits } = this.context.locals.activeUser;

    activeExercise.parameters.forEach((elem) => {
      if (elem === LOAD && coachUnits === "imperial") {
        newSet[mapParameter(elem)] = _.round(
          convert(this.state[mapParameter(elem)]).from("lb").to("kg"),
          2
        );
      } else {
        newSet[mapParameter(elem)] = this.state[mapParameter(elem)];
      }
    });

    const rpeError = splitRpeText(newSet);
    if (rpeError) {
      this.context.locals.flash("error", i18n.t(`common:${rpeError}`));
      return;
    }

    this.props.handleAddSetLocally(exId, setIndex, newSet);

    this._handleCloseAddSet(e);
  }

  _handleNewMovementChange(value, exId, setIndex) {
    const { handleQuickEditChange } = this.props;

    if (value) {
      this.setState({ newMovement: `${value.movementLabel}` });
      const e = {
        target: { name: "exerciseType", value },
      };

      handleQuickEditChange(e, exId, setIndex);
    }
  }

  _handleCreateSuperset(fullExercise, exercise, nextExercise) {
    submit(`/actions/exercise/superset/add`, {
      method: "POST",
      body: {
        exercise,
        nextExercise,
        fullExercise,
      },
    });
  }

  _handleRemoveSuperset(fullExercise, exercise) {
    submit(`/actions/exercise/superset/remove`, {
      method: "POST",
      body: {
        exercise,
        fullExercise,
      },
    });
  }

  // handles expanding the outcomes in the actual results
  toggleExpanded() {
    this.setState({ expanded: !this.state.expanded });
  }

  render() {
    const { t } = this.props;
    const { query, withWeight } = this.context.locals;
    const {
      exercises,
      exercise,
      exerciseOrder,
      fromMetric,
      activeUser,
      handleDuplicate,
      isProgramEditor,
      openQuickEditMode,
      handleQuickEditChange,
      loadInputs,
      intensityInputs,
      isQuickEditOpen,
      LineItemComponent = ExerciseLineItem,
    } = this.props;
    const {
      isAddingSet,
      currentRow,
      load,
      sets,
      rpe,
      reps,
      percentRepMax,
      newMovement,
      expanded,
    } = this.state;
    const newSet = {
      sets,
      load,
      rpe,
      reps,
      percentRepMax,
    };
    const activeUserToRequest = this.context.locals.activeUserToRequest;
    const activeExercise = _.find(exercises, { _id: exercise });
    const supersets = buildSupersets(exercises);
    const activeExerciseIndex = _.findIndex(
      exercises,
      (ex) => ex._id === activeExercise._id
    );

    if (activeExercise?.sets) {
      const isFirst = exerciseOrder === 0;
      const isLast = exerciseOrder === exercises.length - 1;
      // Section membership is derived from order (see groupExercisesBySection),
      // so moving an exercise past a section separator simply reassigns it to
      // the adjacent section - move up/down use the whole-list bounds, not the
      // section's. isSectionLast is still used below to keep supersets from
      // being started across a section boundary.
      const { isSectionLast } = this.props;
      const moveUpDisabled = isFirst;
      const moveDownDisabled = isLast;
      const nextExercise = !isLast && exercises[activeExerciseIndex + 1];
      const isSuperset = activeExercise.nextSuperset === nextExercise._id;
      const isComplex = !!activeExercise.complex;
      const nextIsComplex = !!nextExercise?.complex;
      const prevHandleReorder = (e) =>
        this.handleReorder({
          exercise: activeExercise._id,
          exercises,
          to: -1,
          isProgramEditor,
          athlete: query.athlete,
          e,
        });
      const nextHandleReorder = (e) =>
        this.handleReorder({
          exercise: activeExercise._id,
          exercises,
          to: 1,
          isProgramEditor,
          athlete: query.athlete,
          e,
        });

      let prefix = "";
      if (activeExerciseIndex >= 0) {
        prefix =
          this.props.sectionPrefix ??
          calculatePrefixForExercise(activeExercise._id, supersets);
      }
      const prefixHasNumber = this.hasNumber(prefix);
      const handleWorkoutParamsInput = ({ value, field }) =>
        handleQuickEditChange(
          { target: { name: field, value } },
          activeExercise._id,
          null
        );

      const isAnySetCompleted = anySetCompleted(activeExercise);
      const isAnySetCompletedNext =
        nextExercise && anySetCompleted(nextExercise);
      const intensityMeasure =
        _.get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
          ? "RPE"
          : "RiR";
      const isRiR = intensityMeasure === "RiR";
      const editable = !activeExercise.sets.some((set) => set.outcomes);
      return (
        <>
          <li
            className={`Program-Editor-Exercise ${this.props.altClass || ""} ${
              isNotMac() ? "padding-scroll-no-mac" : ""
            } ${isSuperset ? "is-superset" : ""} ${
              prefixHasNumber && prefix.slice(1) != "1"
                ? "not-first-superset"
                : ""
            } ${
              prefixHasNumber && activeExercise.nextSuperset
                ? "not-last-superset"
                : ""
            } ${isLast ? "last-exercise-list" : ""} ${
              isFirst ? "first-exercise-list" : ""
            } ${isAnySetCompleted && !isProgramEditor ? "is-actuals" : ""}`}
            data-id={activeExercise._id}
            id={activeExercise._id}
            onClick={(e) => {
              if (editable && !e.target.closest("[data-workout-params]")) {
                openQuickEditMode(e);
              }
            }}
          >
            {_.map(activeExercise.sets, (set, setIndex) => {
              return (
                <LineItemComponent
                  key={`${activeExercise._id}${setIndex}`}
                  handleQuickEditChange={handleQuickEditChange}
                  _handleOpenAddSet={this._handleOpenAddSet}
                  _handleCloseAddSet={this._handleCloseAddSet}
                  _handleNewSetChange={this._handleNewSetChange}
                  _handleSaveNewSet={this._handleSaveNewSet}
                  _handleNewMovementChange={this._handleNewMovementChange}
                  set={set}
                  newSet={newSet}
                  setIndex={setIndex}
                  activeExercise={activeExercise}
                  stateExercise={this.props.stateExercise}
                  isProgramEditor={isProgramEditor}
                  fromMetric={fromMetric}
                  activeUser={activeUser}
                  withWeight={withWeight}
                  loadInputs={loadInputs}
                  intensityInputs={intensityInputs}
                  localLoadInputs={this.localLoadInputs}
                  localIntensityInputs={this.localIntensityInputs}
                  isQuickEditOpen={isQuickEditOpen}
                  isAddingSet={isAddingSet}
                  currentRow={currentRow}
                  units={this.context.locals.activeUser.units}
                  newMovement={newMovement}
                  setLength={activeExercise.sets.length}
                  activeContextUser={activeUserToRequest}
                  activeExerciseNotes={activeExercise.notes}
                  exercises={exercises}
                  _handleSaveNewSetForOther={this._handleSaveNewSetForOther}
                  prevHandleReorder={prevHandleReorder}
                  nextHandleReorder={nextHandleReorder}
                  isFirst={moveUpDisabled}
                  isLast={moveDownDisabled}
                  isAnySetCompleted={isAnySetCompleted}
                  prefix={prefix}
                  expanded={expanded}
                  toggleExpanded={this.toggleExpanded}
                  intensityMeasure={intensityMeasure}
                  isRiR={isRiR}
                  editable={editable}
                  handleDuplicate={handleDuplicate}
                />
              );
            })}

            <div data-workout-params>
              <WorkoutParamsRow
                exercise={activeExercise}
                newExercise={
                  (this.props.exercisesInState || []).find(
                    (ex) => ex._id === activeExercise._id
                  ) || activeExercise
                }
                editDayMode={isQuickEditOpen}
                editable={editable}
                handleInput={handleWorkoutParamsInput}
                exerciseId={activeExercise._id}
              />
            </div>

            <NotesWrapper
              isProgramEditor={isProgramEditor}
              openQuickEditMode={isQuickEditOpen}
              onInputHandleQuickEditChange={(e) =>
                handleQuickEditChange(e, activeExercise._id, null)
              }
              activeExercise={activeExercise}
            />

            {isQuickEditOpen &&
              nextExercise &&
              !isLast &&
              !isSectionLast &&
              !isSuperset &&
              !isAnySetCompleted &&
              !isAnySetCompletedNext &&
              !isComplex &&
              !nextIsComplex && (
                <button
                  data-button-type="text"
                  className="superset-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    this._handleCreateSuperset(
                      activeExercise,
                      activeExercise._id,
                      nextExercise._id
                    );
                  }}
                  type="button"
                >
                  <img
                    src="/img/new-icons/add-gradient-icon.svg"
                    height={16}
                    width={16}
                    alt="create superset"
                  />
                  <span>{t("editorSessionTableRow.superset")}</span>
                </button>
              )}

            {isQuickEditOpen && nextExercise && !isLast && isSuperset && (
              <button
                data-button-type="text"
                className="superset-button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  this._handleRemoveSuperset(
                    activeExercise,
                    activeExercise._id,
                    nextExercise._id
                  );
                }}
                type="button"
              >
                <img
                  src="/img/new-icons/red-trash.svg"
                  height={16}
                  width={16}
                  alt="remove superset"
                />
                <span
                  style={{
                    background: "none",
                    color: "var(--danger)",
                  }}
                >
                  {t("editorSessionTableRow.removeSuperset")}
                </span>
              </button>
            )}
          </li>
          <ExerciseMetrics
            exercise={activeExercise}
            fromMetric={fromMetric}
            withWeight={withWeight}
            units={this.context.locals.activeUser.units}
            isProgramEditor={isProgramEditor}
            intensityMeasure={intensityMeasure}
            isRiR={isRiR}
          />
        </>
      );
    }
    return null;
  }
}

EditorSessionTableRow.contextType = RillContext;

export default withTranslation("dashboard")(EditorSessionTableRow);
